import { useCallback, useMemo } from "react"
import { compact, first, get, isEmpty, keyBy, size } from "lodash"

import { useVenue } from "providers/venue"
import { useIndoorMap } from "providers/venue/modules/indoormap/hooks/useIndoorMap"
import { featureCollection } from "@turf/helpers"
import { getSuitablyValueBetweenBearings } from "components/IndoorMap"
import { IAllVenuesViewConfig } from "services/venue"
import { CreateMapViewByVenue } from "./interface"

export const useGetVenueView = () => {
  const { mapConfig, dataLoaded, venues } = useVenue()
  const { indoorRef, mapLoaded, defaultBearing, defaultPitch } = useIndoorMap()
  const mapReady = indoorRef?.current && mapLoaded && dataLoaded

  const allVenuesViewConfig = useMemo(
    () => get<IAllVenuesViewConfig>(mapConfig, "all_venues_view", {}),
    [mapConfig]
  )

  const createMapViewByVenue = useCallback<CreateMapViewByVenue>(
    (venue, overrideMapViewOption = {}) => {
      if (!mapReady) return {}
      const indoorMap = indoorRef.current

      // Calculate default zoom and center using the extent of the venue(s).
      const venueToCaculate = Array.isArray(venue) ? compact(venue) : [venue]
      const hasVenue = !isEmpty(venueToCaculate)
      const venueFeatures = featureCollection(venueToCaculate)

      const venueExtent = hasVenue
        ? indoorMap.getFeatureExtent(venueFeatures)
        : null
      const centerVenueExtent = venueExtent
        ? indoorMap.getExtentCenter(venueExtent)
        : null
      const zoomVenueExtent = venueExtent
        ? indoorMap.getExtentZoom(venueExtent) + 1
        : null //adjust offset

      // Prepare default zoom: prioritize current venue > map config (default_zoom) > zoom extent.
      const currentVenueZoom = get(first(venueToCaculate), "properties.zoom")
      const venueDefaultZoom =
        currentVenueZoom || get(mapConfig, "default_zoom") || zoomVenueExtent

      // Preparing default center:
      const venueDisplayCoordinates =
        size(venueToCaculate) > 1
          ? centerVenueExtent
          : get(first(venueToCaculate), "properties.display_point.coordinates")

      const venueViewConfig = {
        pitch: get(overrideMapViewOption, "pitch", defaultPitch),
        center: get(overrideMapViewOption, "center", venueDisplayCoordinates),
        zoom: get(overrideMapViewOption, "zoom", venueDefaultZoom),
      }

      // Preparing bearing
      const targetBearing = get(
        overrideMapViewOption,
        "bearing",
        defaultBearing
      )
      const currentBearing = indoorMap.getBearing()
      const bearing = getSuitablyValueBetweenBearings(
        targetBearing,
        currentBearing
      )

      return {
        ...venueViewConfig,
        bearing,
      }
    },
    [indoorRef, mapConfig, defaultBearing, defaultPitch, mapReady]
  )

  const venueViewObjects = useMemo(() => {
    if (!mapReady) return {}
    const overallView = [
      { id: "ALL", ...createMapViewByVenue(venues, allVenuesViewConfig) },
    ]
    const venueViews = venues.map((venue) => ({
      id: venue?.id,
      ...createMapViewByVenue(venue),
    }))
    const views = overallView.concat(venueViews)
    return keyBy(views, "id")
  }, [mapReady, venues, createMapViewByVenue, allVenuesViewConfig])

  return venueViewObjects
}
